import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { BQLink } from '../../../../atoms/bqlink';
import Loader from '../../../../atoms/icons/loader';
import { ArrowRightIcon } from '../../../../atoms/material-icons';
import EmptyState from '../empty-state';
import { isWithinTradingHours, widgetLink } from '../../../../../helpers/utils';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import './open-interest-breakup-options.m.css';

const OpenInterestBreakupOptions = ({ widget, external = false, widgetURL = false }) => {
  const widgetParam = widgetURL ? '?view=apps' : '';
  const [openInterestBreakupData, setOpeninterestBreakupData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('stock');
  const [initialLoading, setInitialLoading] = useState(true);
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );
  const queryKey =
    widget === 'break-up' ? 'getOpenInterestBreakupData' : 'getOpenInterestChangeData';

  const getOpenInterestBreakupDataForQuery = async (type = 'stock') => {
    const response = await fetch(
      `/foapi/option/getFOBreakUp?instrument=${type === 'stock' ? '2' : '1'}&lastExpiry=${widget === 'expiry' ? 'true' : 'false'}`,
    );
    return response.json();
  };

  const resultOpenInterestBreakupData = useQuery(
    queryKey,
    () => getOpenInterestBreakupDataForQuery(),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        if (data?.status) {
          setOpeninterestBreakupData(data?.data);
        } else {
          setOpeninterestBreakupData([]);
        }
      },
      enabled: initialLoading || isWithinTradingHours(tradingHolidays, specialTradingDays),
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (resultOpenInterestBreakupData?.isSuccess) {
      setOpeninterestBreakupData(resultOpenInterestBreakupData?.data?.data);
    }
  }, []);

  const clickHandler = async (type) => {
    setLoading(true);
    setSelectedFilter(type);
    const data = await getOpenInterestBreakupDataForQuery(type);
    if (data?.status) {
      setOpeninterestBreakupData(data?.data);
    } else {
      setOpeninterestBreakupData([]);
    }
    setLoading(false);
  };

  return (
    <React.Fragment>
      {initialLoading ? (
        <div styleName="initial-loading-container"></div>
      ) : (
        <div styleName={`open-interest-breakup-container ${external ? 'external' : ''}`}>
          <div styleName="title-container">
            <span styleName="title">
              {widget === 'break-up'
                ? 'CURRENT F&O OPEN INTEREST BREAK-UP'
                : 'F&O OPEN INTEREST CHANGE SINCE LAST EXPIRY'}
            </span>
            <div styleName="filter-btn-container">
              <button
                styleName={`filter-btn ${selectedFilter === 'stock' ? 'filter-selected' : ''}`}
                onClick={() => clickHandler('stock')}
              >
                Stock Options
              </button>
              <button
                styleName={`filter-btn ${selectedFilter === 'index' ? 'filter-selected' : ''}`}
                onClick={() => clickHandler('index')}
              >
                Index Options
              </button>
            </div>
          </div>
          {openInterestBreakupData && openInterestBreakupData?.length !== 0 ? (
            <>
              {loading ? (
                <div styleName="loading-container-ext">
                  <div styleName="loading-container-int">
                    <Loader color={'#dadee1'} />
                  </div>
                </div>
              ) : (
                <div styleName="content-container">
                  <div styleName="table-body-container">
                    <table className="desktop-only">
                      <thead>
                        <tr styleName="initial-header-container">
                          <th colSpan={1}></th>
                          <th colSpan={2}>Futures</th>
                          <th colSpan={2}>Calls</th>
                          <th colSpan={2}>Puts</th>
                          <th colSpan={2}>Total</th>
                          {widget === 'break-up' ? <th colSpan={2}></th> : null}
                        </tr>
                        <tr styleName="second-header-container">
                          <th styleName="column-title">Security</th>
                          <th
                            styleName={`column-title-alt ${widget === 'expiry' ? 'highlighted-container' : ''}`}
                          >
                            Open Interest
                            <br />
                            (In Contracts)
                          </th>
                          <th
                            styleName={`column-title-alt ${widget === 'expiry' ? 'highlighted-container' : ''}`}
                          >
                            OI % Change
                          </th>
                          <th
                            styleName={`column-title-alt ${widget === 'break-up' ? 'highlighted-container' : ''}`}
                          >
                            Open Interest
                            <br />
                            (In Contracts)
                          </th>
                          <th
                            styleName={`column-title-alt ${widget === 'break-up' ? 'highlighted-container' : ''}`}
                          >
                            OI % Change
                          </th>
                          <th
                            styleName={`column-title-alt ${widget === 'expiry' ? 'highlighted-container' : ''}`}
                          >
                            Open Interest
                            <br />
                            (In Contracts)
                          </th>
                          <th
                            styleName={`column-title-alt ${widget === 'expiry' ? 'highlighted-container' : ''}`}
                          >
                            OI % Change
                          </th>
                          <th
                            styleName={`column-title-alt ${widget === 'break-up' ? 'highlighted-container' : ''}`}
                          >
                            Open Interest
                            <br />
                            (In Contracts)
                          </th>
                          <th
                            styleName={`column-title-alt ${widget === 'break-up' ? 'highlighted-container' : ''}`}
                          >
                            OI % Change
                          </th>
                          {widget === 'break-up' ? (
                            <React.Fragment>
                              <th styleName="column-title-alt">1 Month OI(%)</th>
                              <th styleName="column-title-alt">Rollover (%)</th>
                            </React.Fragment>
                          ) : null}
                        </tr>
                      </thead>
                      <tbody styleName="table-body">
                        {Array.isArray(openInterestBreakupData) &&
                          openInterestBreakupData?.slice(0, 8)?.map((dataPoint, index) => (
                            <tr styleName="data-row" key={index}>
                              <td>
                                {dataPoint?.['symbol-strike'] ? (
                                  <span styleName="name">{dataPoint?.['symbol-strike']}</span>
                                ) : (
                                  <span styleName="name">-</span>
                                )}
                              </td>
                              <td
                                styleName={`column-value ${widget === 'expiry' ? 'highlighted-container' : ''}`}
                              >
                                {dataPoint?.['future-open-interest'] ? (
                                  <span>{dataPoint?.['future-open-interest']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td
                                styleName={`column-value ${widget === 'expiry' ? 'highlighted-container' : ''}`}
                              >
                                {dataPoint?.['future-open-interest-change-percentage'] ? (
                                  <span>
                                    {String(
                                      dataPoint?.['future-open-interest-change-percentage'],
                                    ).includes('-') ? (
                                      <span styleName="negative-value">
                                        {dataPoint?.[
                                          'future-open-interest-change-percentage'
                                        ]?.toFixed(2)}
                                        %
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint?.['future-open-interest-change-percentage']?.toFixed(2)}%`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00%</span>
                                )}
                              </td>
                              <td
                                styleName={`column-value ${widget === 'break-up' ? 'highlighted-container' : ''}`}
                              >
                                {dataPoint?.['call-open-interest'] ? (
                                  <span>{dataPoint?.['call-open-interest']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td
                                styleName={`column-value ${widget === 'break-up' ? 'highlighted-container' : ''}`}
                              >
                                {dataPoint?.['call-open-interest-change-percentage'] ? (
                                  <span>
                                    {String(
                                      dataPoint?.['call-open-interest-change-percentage'],
                                    ).includes('-') ? (
                                      <span styleName="negative-value">
                                        {dataPoint?.[
                                          'call-open-interest-change-percentage'
                                        ]?.toFixed(2)}
                                        %
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint?.['call-open-interest-change-percentage']?.toFixed(2)}%`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00%</span>
                                )}
                              </td>
                              <td
                                styleName={`column-value ${widget === 'expiry' ? 'highlighted-container' : ''}`}
                              >
                                {dataPoint?.['put-open-interest'] ? (
                                  <span>{dataPoint?.['put-open-interest']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td
                                styleName={`column-value ${widget === 'expiry' ? 'highlighted-container' : ''}`}
                              >
                                {dataPoint?.['put-open-interest-change-percentage'] ? (
                                  <span>
                                    {String(
                                      dataPoint?.['put-open-interest-change-percentage'],
                                    ).includes('-') ? (
                                      <span styleName="negative-value">
                                        {dataPoint?.[
                                          'put-open-interest-change-percentage'
                                        ]?.toFixed(2)}
                                        %
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint?.['put-open-interest-change-percentage']?.toFixed(2)}%`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00%</span>
                                )}
                              </td>
                              <td
                                styleName={`column-value ${widget === 'break-up' ? 'highlighted-container' : ''}`}
                              >
                                {dataPoint?.['total-open-interest'] ? (
                                  <span>{dataPoint?.['total-open-interest']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td
                                styleName={`column-value ${widget === 'break-up' ? 'highlighted-container' : ''}`}
                              >
                                {dataPoint?.['total-open-interest-change-percentage'] ? (
                                  <span>
                                    {String(
                                      dataPoint?.['total-open-interest-change-percentage'],
                                    ).includes('-') ? (
                                      <span styleName="negative-value">
                                        {dataPoint?.[
                                          'total-open-interest-change-percentage'
                                        ]?.toFixed(2)}
                                        %
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint?.['total-open-interest-change-percentage']?.toFixed(2)}%`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00%</span>
                                )}
                              </td>
                              {widget === 'break-up' ? (
                                <React.Fragment>
                                  <td styleName="column-value">
                                    {dataPoint?.['1m-open-interest-change-percentage'] ? (
                                      <span>
                                        {dataPoint?.['1m-open-interest-change-percentage']?.toFixed(
                                          2,
                                        )}
                                        %
                                      </span>
                                    ) : (
                                      <span>0.00%</span>
                                    )}
                                  </td>
                                  <td styleName="column-value">
                                    {dataPoint?.['rollover-percentage'] ? (
                                      <span>{dataPoint?.['rollover-percentage']?.toFixed(2)}%</span>
                                    ) : (
                                      <span>0.00%</span>
                                    )}
                                  </td>
                                </React.Fragment>
                              ) : null}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    <table className="mobile-only">
                      <thead styleName="table-header-mobile">
                        <tr>
                          <th styleName="column-title">Security</th>
                          <th styleName="column-title-alt">
                            Futures <br /> OI
                          </th>
                          <th styleName="column-title-alt">
                            Call
                            <br /> OI
                          </th>
                          <th styleName="column-title-alt">
                            Put <br />
                            OI
                          </th>
                          <th styleName="column-title-alt">
                            Total <br />
                            OI
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(openInterestBreakupData) &&
                          openInterestBreakupData?.slice(0, 8)?.map((dataPoint, index) => (
                            <tr styleName="data-row-alt" key={index}>
                              <td>
                                {dataPoint?.['symbol-strike'] ? (
                                  <span styleName="name">{dataPoint?.['symbol-strike']}</span>
                                ) : (
                                  <span styleName="name">-</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.['future-open-interest'] ? (
                                  <span>{dataPoint?.['future-open-interest']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.['call-open-interest'] ? (
                                  <span>{dataPoint?.['call-open-interest']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.['put-open-interest'] ? (
                                  <span>{dataPoint?.['put-open-interest']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint?.['total-open-interest'] ? (
                                  <span>{dataPoint?.['total-open-interest']}</span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div styleName="no-data-container-ext">
              <div styleName="no-data-container-int">
                <EmptyState />
              </div>
            </div>
          )}

          {!external && (
            <div styleName="view-btn-container">
              <BQLink
                styleName="view-more-btn"
                href={
                  widget === 'break-up'
                    ? widgetLink(
                        `/markets/option-chain/open-interest-breakup${widgetParam}`,
                        widget,
                      )
                    : widgetLink(
                        `/markets/option-chain/futures-open-interest-change-since-last-expiry${widgetParam}`,
                        widget,
                      )
                }
                target="_blank"
                rel="noreferrer"
              >
                View All Options
                <ArrowRightIcon styleName="tab-icon" color={'#2b2b2b'} />
              </BQLink>
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default OpenInterestBreakupOptions;
